import { useTranslation } from 'react-i18next';

import { BankAccountResponseDto, IdentityVerificationResponseDto, MyProfileResponseDto } from '@repo/services/hooks';
import styled from 'styled-components';

import { translation } from 'utils/translation';

import Flex from 'components/util/Flex';

import { BankAccountModificationButton, BankAccountVerificationButton } from './BankAccountButton';
import { EmailModificationButton, EmailVerificationButton } from './EmailButton';
import { IdentityVerificationButton } from './IdentityButton';
import { SAdditionalInformation, SInformationItem } from '../shared/style';

type CreatorInformationProps = {
  profileData?: MyProfileResponseDto;
  bankAccountData?: BankAccountResponseDto;
  identityData?: IdentityVerificationResponseDto;
};

function CreatorInformation({ profileData, identityData, bankAccountData }: CreatorInformationProps) {
  const { t } = useTranslation();
  // 본인 인증 완료 여부
  const isIdentityVerificationCompleted = Boolean(identityData);
  // 이메일 인증 완료 여부
  const isEmailVerificationCompleted = profileData?.verified ?? false;

  // 계좌 인증 가능 여부
  const isBankAccountVerificationEnabled =
    (isEmailVerificationCompleted && isIdentityVerificationCompleted) || bankAccountData?.status === 'verified';
  // 계좌 인증 완료 여부
  const isBankAccountVerificationCompleted = bankAccountData?.status === 'verified';

  // 은행 계좌가 등록되었는지 여부 판단 (Admin에서 본인 인증을 풀 수 있는 엣지 케이스 대응)
  const isBankAccountAssigned = bankAccountData?.status === 'verified';

  const rowGap = isBankAccountVerificationCompleted ? 10 : 12;

  return (
    <>
      <Flex direction="column" rowGap={rowGap}>
        {/* 유저이름 */}
        <SInformationItemWrapper $paddingBottom={16} $paddingTop={16}>
          <span>{t('chat.rewards_creator_information_username')}</span>
          <span className="bold">{profileData?.username}</span>
        </SInformationItemWrapper>
        {/* 본인 인증 여부 */}
        <SInformationItemWrapper>
          <span>{t('chat.rewards_creator_information_identity_verification')}</span>
          <Flex align="center" columnGap={16}>
            <span className="bold">{identityData?.name}</span>
            {isIdentityVerificationCompleted ? (
              <IdentityVerificationButton
                identityData={identityData!}
                isVerificationCompleted={isIdentityVerificationCompleted}
              />
            ) : (
              <IdentityVerificationButton isVerificationCompleted={isIdentityVerificationCompleted} />
            )}
          </Flex>
        </SInformationItemWrapper>
        {/* 이메일 인증 여부 */}
        <SInformationItemWrapper>
          <span className="no-wrap">{t('chat.rewards_creator_information_email')}</span>
          <Flex align="center" columnGap={16} justify="flex-end" className="email-wrapper">
            <p className="bold email ellipsis">{profileData?.email}</p>
            {isEmailVerificationCompleted ? (
              <EmailModificationButton />
            ) : (
              <EmailVerificationButton isVerificationCompleted={isEmailVerificationCompleted} />
            )}
          </Flex>
        </SInformationItemWrapper>
        {/* 계좌 */}
        <SBankInformationItemWrapper $isBankAccountVerificationCompleted={isBankAccountVerificationCompleted}>
          <Flex direction="column" isExpanded rowGap={10}>
            {/* 계좌 인증 여부 */}
            <Flex justify="space-between" align="center">
              <span>{t('chat.rewards_creator_information_account_verification')}</span>
              {isBankAccountVerificationCompleted ? (
                <BankAccountModificationButton />
              ) : (
                <BankAccountVerificationButton
                  isVerificationEnabled={isBankAccountVerificationEnabled}
                  isVerificationCompleted={isBankAccountVerificationCompleted}
                />
              )}
            </Flex>
            {/* 은행 정보 */}
            {isBankAccountAssigned && (
              <BankAccountInformation
                bankName={bankAccountData.bankName}
                accountHolder={bankAccountData.accountHolder}
                accountNumber={bankAccountData.accountNumber}
              />
            )}
          </Flex>
        </SBankInformationItemWrapper>
      </Flex>
      {/* 본인 인증 및 이메일 인증 체크 */}
      {!isBankAccountVerificationEnabled && (
        <SAdditionalInformation>
          <span>{t('chat.rewards_verify_identify_email_account')}</span>
        </SAdditionalInformation>
      )}
    </>
  );
}

export default CreatorInformation;

type SInformationItemWrapperProps = {
  $paddingTop?: number;
  $paddingBottom?: number;
};
const SInformationItemWrapper = styled(SInformationItem)<SInformationItemWrapperProps>`
  padding-top: ${({ $paddingTop }) => ($paddingTop ? `${$paddingTop}px` : '0px')};
  padding-bottom: ${({ $paddingBottom }) => ($paddingBottom ? `${$paddingBottom}px` : '12px')};

  .email-wrapper {
    width: calc(100% - 40px);
  }

  .email {
    max-width: 55%;
  }
`;

type SBankInformationItemWrapperProps = {
  $isBankAccountVerificationCompleted: boolean;
};

const SBankInformationItemWrapper = styled(SInformationItem)<SBankInformationItemWrapperProps>`
  padding: ${({ $isBankAccountVerificationCompleted }) =>
    $isBankAccountVerificationCompleted ? '0px 0px 10px 0' : '0 0 12px 0px'};
`;

const SBankAccountInformation = styled.div`
  padding: 10px 0px;

  ${({ theme }) => theme.font.body3_m};

  .bold {
    ${({ theme }) => theme.font.body3_m};
  }
`;

type BankAccountInformationProps = {
  accountHolder: string; // 예금주명
  bankName: string; // 은행명
  accountNumber: string; // 계좌번호
};
const BankAccountInformation = ({ accountHolder, accountNumber, bankName }: BankAccountInformationProps) => {
  return (
    <Flex direction="column" isExpanded>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_account_holder')}</span>
          <span className="bold">{accountHolder}</span>
        </Flex>
      </SBankAccountInformation>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_bank')}</span>
          <span className="bold">{bankName}</span>
        </Flex>
      </SBankAccountInformation>
      <SBankAccountInformation>
        <Flex align="center" justify="space-between">
          <span>{translation('chat.rewards_account_number')}</span>
          <span className="bold">{accountNumber}</span>
        </Flex>
      </SBankAccountInformation>
    </Flex>
  );
};
